import ButtonIconPicker from 'client/components/core/Sidebar/components/Settings/components/ButtonIconPicker'
import FontSize from 'client/components/core/Sidebar/components/Settings/components/Font/FontSize'
import TextFontUpdater from 'client/components/core/Sidebar/components/Settings/components/Font/TextFontUpdater'
import HoverButtonSetting from 'client/components/core/Sidebar/components/Settings/components/HoverButton'
import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import DelayOption from 'client/components/core/Sidebar/components/Settings/options/DelayOption'
import DeviceAppearanceNew from 'client/components/core/Sidebar/components/Settings/options/DeviceAppearance/DeviceAppearanceNew'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import {
  FlexAlignStyle,
  ShadowStyle,
} from 'client/components/core/Sidebar/components/Settings/styles'
import ButtonWidth from 'client/components/core/Sidebar/components/Settings/styles/ButtonWidth'
import TextLineHeight from 'client/components/core/Sidebar/components/Settings/styles/TextLineHeight'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { TextInputControl } from 'src/editor/shared/settings/controls/text-input-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { EntityBorderGroup } from 'src/editor/shared/settings/wrappers/border-groups/entity-border-group'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { CommonButtonEntityInterface } from 'src/shared/schemas/buttons/common-button-entity.schema'
import { getMobileOrDesktop } from 'src/shared/utils/get-mobile-or-desktop'
import { useCalculateButtonStyles } from '../../hooks/use-calculate-button-styles'
import { ResetEntityStyles } from '../reset-entity-styles-button'

export const CommonButtonSettings = ({
  entity,
  defaultEntity,
  hideDeviceAppearance,
}: {
  entity: CommonButtonEntityInterface
  defaultEntity?: CommonButtonEntityInterface
  hideDeviceAppearance?: boolean
}) => {
  const { t } = useTypedTranslation()
  const { isMobile } = useDeviceModeContext()
  const updateProp = useUpdateProp(entity)
  const updateEntity = useUpdateEntity<CommonButtonEntityInterface>()

  const {
    calculatedBorder,
    calculatedPadding,
    calculatedFontSize,
    calculatedLineHeight,
    calculatedTextColor,
    calculatedBackground,
    calculatedHover,
  } = useCalculateButtonStyles({
    border: entity.border,
    mobileBorder: entity.mobileBorder,
    padding: entity.padding,
    mobilePadding: entity.mobilePadding,
    fontSize: entity.textFontSize,
    mobileFontSize: entity.mobileTextFontSize,
    lineHeight: entity.lineHeight,
    mobileLineHeight: entity.mobileLineHeight,
    textColor: entity.textColor,
    mobileTextColor: entity.mobileTextColor,
    background: entity.background,
    mobileBackground: entity.mobileBackground,
    backgroundColor: entity.backgroundColor,
    mobileBackgroundColor: entity.mobileBackgroundColor,
    hover: entity.hover,
    mobileHover: entity.mobileHover,
    buttonType: entity.buttonType,
  })

  return (
    <>
      <FlexAlignStyle
        align={entity.alignSelf}
        mobileAlign={entity.mobileAlignSelf}
        update={updateProp('alignSelf')}
        mobileUpdate={updateProp('mobileAlignSelf')}
      />
      <ButtonWidth
        update={updateProp('width')}
        mobileUpdate={updateProp('mobileWidth')}
        width={entity.width}
        mobileWidth={entity.mobileWidth}
      />
      <SizeAndPositionGroup
        padding={
          isMobile ? calculatedPadding.mobile : calculatedPadding.desktop
        }
        defaultPadding={
          isMobile ? defaultEntity?.mobilePadding : defaultEntity?.padding
        }
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        defaultMargin={
          isMobile ? defaultEntity?.mobileMargin : defaultEntity?.margin
        }
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <GroupTitle
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={[
                'textFontSize',
                'textFontFileId',
                'textFontFamily',
                'textFontWeight',
                'textFontStyle',
                'subTextFontSize',
                'subTextFontFileId',
                'subTextFontFamily',
                'subTextFontWeight',
                'subTextFontStyle',
              ]}
              mobileKeys={[
                'mobileTextFontSize',
                'mobileTextFontFileId',
                'mobileTextFontFamily',
                'mobileTextFontWeight',
                'mobileTextFontStyle',
                'mobileSubTextFontSize',
                'mobileSubTextFontFileId',
                'mobileSubTextFontFamily',
                'mobileSubTextFontWeight',
                'mobileSubTextFontStyle',
              ]}
            />
          )
        }
      >
        {t('typography')}
      </GroupTitle>
      <TextInputControl
        wrapperProps={{
          labelText: t('entity_settings.button.button_text.label'),
        }}
        value={entity.text}
        update={updateProp('text')}
      />
      <FontSize
        label="settings_styles.font_size.label"
        fontSize={calculatedFontSize.desktop}
        mobileFontSize={calculatedFontSize.mobile}
        onChange={updateProp('textFontSize')}
        onChangeMobile={updateProp('mobileTextFontSize')}
      />
      <TextLineHeight
        fontSize={calculatedFontSize.desktop}
        mobileFontSize={calculatedFontSize.mobile}
        update={updateProp('lineHeight')}
        mobileUpdate={updateProp('mobileLineHeight')}
        lineHeight={calculatedLineHeight.desktop}
        mobileLineHeight={calculatedLineHeight.mobile}
        removeEntityStyle={updateProp('lineHeight')}
        removeEntityMobileStyle={updateProp('mobileLineHeight')}
      />
      <TextFontUpdater<CommonButtonEntityInterface>
        entity={entity}
        update={updateEntity}
        fontFamilyKey={'textFontFamily'}
        fontWeightKey={'textFontWeight'}
        fontStyleKey={'textFontStyle'}
        mobileFontFamilyKey={'mobileTextFontFamily'}
        mobileFontWeightKey={'mobileTextFontWeight'}
        mobileFontStyleKey={'mobileTextFontStyle'}
        fontFileIdKey={'textFontFileId'}
        mobileFontFileIdKey={'mobileTextFontFileId'}
      />
      <TextInputControl
        wrapperProps={{
          labelText: 'entity_settings.button.sub_text.label',
        }}
        update={updateProp('subText')}
        value={entity.subText}
      />
      {entity.subText && (
        <FontSize
          label={'entity_settings.button.sub_text.font_size.label'}
          fontSize={entity.subTextFontSize}
          mobileFontSize={entity.mobileSubTextFontSize}
          onChange={updateProp('subTextFontSize')}
          onChangeMobile={updateProp('mobileSubTextFontSize')}
        />
      )}
      {entity.subText && (
        <TextFontUpdater
          entity={entity}
          update={updateEntity}
          fontFamilyKey={'subTextFontFamily'}
          fontWeightKey={'subTextFontWeight'}
          fontStyleKey={'subTextFontStyle'}
          mobileFontFamilyKey={'mobileSubTextFontFamily'}
          mobileFontWeightKey={'mobileSubTextFontWeight'}
          mobileFontStyleKey={'mobileSubTextFontStyle'}
        />
      )}
      <ButtonIconPicker
        valueBefore={entity.iconClassNameBefore}
        valueAfter={entity.iconClassNameAfter}
        updateBefore={updateProp('iconClassNameBefore')}
        updateAfter={updateProp('iconClassNameAfter')}
      />
      <GroupTitle
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={['background', 'backgroundColor', 'textColor']}
              mobileKeys={[
                'mobileBackground',
                'mobileBackgroundColor',
                'mobileTextColor',
              ]}
            />
          )
        }
      >
        color
      </GroupTitle>
      <ColorPickerControl
        isGradientAvailable
        label="background color"
        update={updateProp('background')}
        mobileUpdate={updateProp('mobileBackground')}
        color={calculatedBackground.desktop}
        mobileColor={calculatedBackground.mobile}
      />
      <ColorPickerControl
        label="text color"
        update={updateProp('textColor')}
        mobileUpdate={updateProp('mobileTextColor')}
        color={calculatedTextColor.desktop}
        mobileColor={calculatedTextColor.mobile}
      />
      {entity.subText && (
        <ColorPickerControl
          label="entity_settings.button.sub_text.color.label"
          update={updateProp('subTextColor')}
          mobileUpdate={updateProp('mobileSubTextColor')}
          color={entity.subTextColor}
          mobileColor={entity.mobileSubTextColor}
          isClearable
        />
      )}
      <EntityBorderGroup
        entity={entity}
        defaultEntity={defaultEntity}
        propertyKey="border"
        mobilePropertyKey="mobileBorder"
        calculateValuesHook={() => {
          return {
            calculatedBorder: getMobileOrDesktop(
              calculatedBorder.desktop,
              calculatedBorder.mobile,
              isMobile,
            ),
          }
        }}
      />

      <ShadowStyle
        shadow={entity.boxShadow}
        mobileShadow={entity.mobileBoxShadow}
        update={updateProp('boxShadow')}
        mobileUpdate={updateProp('mobileBoxShadow')}
      />
      <HoverButtonSetting
        textColor={calculatedTextColor.desktop}
        backgroundColor={calculatedBackground.desktop}
        subTextColor={entity.subTextColor}
        defaultHover={defaultEntity?.hover}
        mobileDefaultHover={defaultEntity?.mobileHover}
        update={updateProp('hover')}
        mobileUpdate={updateProp('mobileHover')}
        hover={calculatedHover.desktop}
        mobileHover={calculatedHover.mobile}
      />
      <DelayOption update={updateProp('delay')} delay={entity.delay} />
      {!hideDeviceAppearance && <DeviceAppearanceNew entity={entity} />}
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}
